/**
 * pixi-particles — Easing.js
 *
 * A compact easing function library plus a name -> function resolver so
 * emitter configs can keep using friendly strings like:
 *
 *   ease: 'Linear'
 *   ease: 'Sine.easeInOut'
 *   ease: 'Back.easeOut'
 *   ease: 'bounce.out'
 *
 * (Both PascalCase.easeIn/Out/InOut and lowercase.in/out/inOut spellings are
 * accepted, matching the two styles Phaser itself has supported over time.)
 *
 * You can also just pass your own `(t) => number` function directly as the
 * `ease` value and it will be used as-is.
 */

export var Easing: Record<string, (t: number, steps?: number) => number> = {

    Linear: function (t) { return t; },

    'Quad.easeIn': function (t) { return t * t; },
    'Quad.easeOut': function (t) { return t * (2 - t); },
    'Quad.easeInOut': function (t) { return (t < 0.5) ? 2 * t * t : -1 + (4 - 2 * t) * t; },

    'Cubic.easeIn': function (t) { return t * t * t; },
    'Cubic.easeOut': function (t) { return (--t) * t * t + 1; },
    'Cubic.easeInOut': function (t) { return (t < 0.5) ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1; },

    'Quart.easeIn': function (t) { return t * t * t * t; },
    'Quart.easeOut': function (t) { return 1 - (--t) * t * t * t; },
    'Quart.easeInOut': function (t) { return (t < 0.5) ? 8 * t * t * t * t : 1 - 8 * (--t) * t * t * t; },

    'Quint.easeIn': function (t) { return t * t * t * t * t; },
    'Quint.easeOut': function (t) { return 1 + (--t) * t * t * t * t; },
    'Quint.easeInOut': function (t) { return (t < 0.5) ? 16 * t * t * t * t * t : 1 + 16 * (--t) * t * t * t * t; },

    'Sine.easeIn': function (t) { return 1 - Math.cos(t * Math.PI / 2); },
    'Sine.easeOut': function (t) { return Math.sin(t * Math.PI / 2); },
    'Sine.easeInOut': function (t) { return 0.5 * (1 - Math.cos(Math.PI * t)); },

    'Expo.easeIn': function (t) { return (t === 0) ? 0 : Math.pow(1024, t - 1); },
    'Expo.easeOut': function (t) { return (t === 1) ? 1 : 1 - Math.pow(2, -10 * t); },
    'Expo.easeInOut': function (t)
    {
        if (t === 0) { return 0; }
        if (t === 1) { return 1; }

        return (t < 0.5) ? 0.5 * Math.pow(1024, (t * 2) - 1) : 0.5 * (-Math.pow(2, -10 * ((t * 2) - 1)) + 2);
    },

    'Circ.easeIn': function (t) { return 1 - Math.sqrt(1 - t * t); },
    'Circ.easeOut': function (t) { return Math.sqrt(1 - (--t) * t); },
    'Circ.easeInOut': function (t)
    {
        t *= 2;

        if (t < 1) { return -0.5 * (Math.sqrt(1 - t * t) - 1); }

        t -= 2;

        return 0.5 * (Math.sqrt(1 - t * t) + 1);
    },

    'Back.easeIn': function (t) { var s = 1.70158; return t * t * ((s + 1) * t - s); },
    'Back.easeOut': function (t) { var s = 1.70158; return (--t) * t * ((s + 1) * t + s) + 1; },
    'Back.easeInOut': function (t)
    {
        var s = 1.70158 * 1.525;

        t *= 2;

        if (t < 1) { return 0.5 * (t * t * ((s + 1) * t - s)); }

        t -= 2;

        return 0.5 * (t * t * ((s + 1) * t + s) + 2);
    },

    'Elastic.easeIn': function (t)
    {
        if (t === 0) { return 0; }
        if (t === 1) { return 1; }

        return -Math.pow(2, 10 * (t - 1)) * Math.sin((t - 1.1) * 5 * Math.PI);
    },
    'Elastic.easeOut': function (t)
    {
        if (t === 0) { return 0; }
        if (t === 1) { return 1; }

        return Math.pow(2, -10 * t) * Math.sin((t - 0.1) * 5 * Math.PI) + 1;
    },
    'Elastic.easeInOut': function (t)
    {
        if (t === 0) { return 0; }
        if (t === 1) { return 1; }

        t *= 2;

        if (t < 1) { return -0.5 * Math.pow(2, 10 * (t - 1)) * Math.sin((t - 1.1) * 5 * Math.PI); }

        return 0.5 * Math.pow(2, -10 * (t - 1)) * Math.sin((t - 1.1) * 5 * Math.PI) + 1;
    },

    'Bounce.easeIn': function (t) { return 1 - Easing['Bounce.easeOut'](1 - t); },
    'Bounce.easeOut': function (t)
    {
        if (t < 1 / 2.75) { return 7.5625 * t * t; }
        else if (t < 2 / 2.75) { t -= 1.5 / 2.75; return 7.5625 * t * t + 0.75; }
        else if (t < 2.5 / 2.75) { t -= 2.25 / 2.75; return 7.5625 * t * t + 0.9375; }
        else { t -= 2.625 / 2.75; return 7.5625 * t * t + 0.984375; }
    },
    'Bounce.easeInOut': function (t)
    {
        return (t < 0.5)
            ? Easing['Bounce.easeIn'](t * 2) * 0.5
            : Easing['Bounce.easeOut'](t * 2 - 1) * 0.5 + 0.5;
    },

    Stepped: function (t, steps)
    {
        if (steps === undefined) { steps = 1; }

        return Math.floor(t * steps) / steps;
    },

    'Bezier': function (t) { return t * t * t + 3 * t * t * (1 - t) * 0 + 3 * t * (1 - t) * (1 - t) * 0 + (1 - t) * (1 - t) * (1 - t) * 0; }
};

// Aliases, so both `'Quad'` (short) and lowercase dotted forms resolve.
var aliasSources = [ 'Quad', 'Cubic', 'Quart', 'Quint', 'Sine', 'Expo', 'Circ', 'Back', 'Elastic', 'Bounce' ];

aliasSources.forEach(function (name)
{
    var lower = name.toLowerCase();

    Easing[name] = Easing[name + '.easeInOut'];
    Easing[lower + '.in'] = Easing[name + '.easeIn'];
    Easing[lower + '.out'] = Easing[name + '.easeOut'];
    Easing[lower + '.inout'] = Easing[name + '.easeInOut'];
    Easing[name + '.easein'] = Easing[name + '.easeIn'];
    Easing[name + '.easeout'] = Easing[name + '.easeOut'];
});

Easing.linear = Easing.Linear;

/**
 * Resolves an ease name (or function, in which case it's just returned) to
 * an easing function. Falls back to Linear for unknown names.
 *
 * @param {string|function} ease
 * @param {any[]} [easeParams] - Extra params, currently only used by 'Stepped'.
 */
export type EaseFn = (t: number) => number;

export function GetEaseFunction (ease: string | EaseFn, easeParams?: any[]): EaseFn
{
    if (typeof ease === 'function')
    {
        return ease;
    }

    if (ease === 'Stepped' && easeParams)
    {
        var steps = easeParams[0];

        return function (t: number) { return (Easing as any).Stepped(t, steps); };
    }

    return (Easing as any)[ease] || Easing.Linear;
}
